/* TeacherHubBrowser — teacher-facing DeskeyHUB

   Lets the teacher:
   - Browse every department's library (read-only content + download)
   - Upload a document into an existing folder of any department (awaiting
     review unless auto-publish is on). Subjects and folders are created by
     the department head, not teachers.
   - Track status of their own submissions across all departments
*/
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Library, FolderOpen, FileText, Upload, Search, X, ArrowLeft, ArrowRight,
  Clock, CheckCircle, XCircle, Eye, ChevronRight, AlertCircle, Send,
  BookOpen, Zap
} from './DeskeyIcons';
import { Button } from './ui/button';
import DocViewer from './DocViewer';
import { friendlyError } from '../lib/errors';
import { useAuthedObjectUrl } from '../lib/authedFile';
import Loader from './Loader';
import { PageHeading, EmptyState, CARD, CARD_HOVER } from '@/components/teacher/teacherUi';

const count = (n, one) => `${n || 0} ${one}${n === 1 ? '' : 's'}`;

const API = process.env.REACT_APP_BACKEND_URL;
const jsonApi = async (p, opts = {}) => {
  const t = localStorage.getItem('desky_token');
  const r = await fetch(`${API}/api${p}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}), ...opts.headers },
  });
  let txt = '';
  try { txt = await r.text(); } catch { /* swallow */ }
  let d = null;
  if (txt) { try { d = JSON.parse(txt); } catch { /* not json */ } }
  if (!r.ok) {
    const fallback = r.status === 400 ? 'Bad request'
      : r.status === 401 ? 'Session expired — please log in again'
      : r.status === 403 ? 'You don\'t have permission for this action'
      : r.status === 404 ? 'Not found'
      : r.status === 409 ? 'Conflict — that name or code is already in use'
      : r.status === 413 ? 'File is too large'
      : r.status === 415 ? 'Unsupported file type'
      : `Request failed (${r.status})`;
    const detail = (d && (d.detail || d.message)) || fallback;
    return { detail, _status: r.status, ...(d || {}) };
  }
  return d || {};
};
const formApi = async (p, fd) => {
  const t = localStorage.getItem('desky_token');
  let r;
  try {
    r = await fetch(`${API}/api${p}`, { method: 'POST', headers: { ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: fd });
  } catch {
    return { detail: 'Network error — the file was not uploaded', _status: 0 };
  }
  let d = null;
  try { d = await r.json(); } catch { /* non-JSON error body (proxy 413, HTML 502…) */ }
  // Callers treat a missing `detail` as success. Without this an upload that
  // failed with a non-JSON body closed the modal as if it had worked, and the
  // document then never showed up under My Submissions.
  if (!r.ok) {
    return {
      ...(d || {}),
      detail: d?.detail || (r.status === 413 ? 'File is too large' : `Upload failed (HTTP ${r.status})`),
      _status: r.status,
    };
  }
  return d || {};
};
const fmtBytes = (b) => { if (!b) return '0 B'; const u = ['B','KB','MB','GB']; let i=0,n=b; while(n>=1024&&i<u.length-1){n/=1024;i++;} return `${n.toFixed(n>=10||i===0?0:1)} ${u[i]}`; };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';

const fileBadge = (mime, name) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if ((mime || '').includes('pdf') || ext === 'pdf') return { bg: '#ef4444', label: 'PDF' };
  if (['doc', 'docx'].includes(ext)) return { bg: '#2563eb', label: 'DOC' };
  if (['xls', 'xlsx'].includes(ext)) return { bg: '#059669', label: 'XLS' };
  if (['ppt', 'pptx'].includes(ext)) return { bg: '#ea580c', label: 'PPT' };
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return { bg: '#8b5cf6', label: 'IMG' };
  return { bg: '#64748b', label: ext?.toUpperCase() || 'FILE' };
};

export default function TeacherHubBrowser() {
  const A = '#1A7F37'; // Deskey green at 5:1 on white, for fills and text
  const [tab, setTab] = useState('browse'); // browse | my-submissions
  const [depts, setDepts] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mySubs, setMySubs] = useState([]);

  const loadDepts = useCallback(async () => {
    setLoading(true);
    const r = await jsonApi('/hub/departments');
    setDepts(r?.departments || []);
    setLoading(false);
  }, []);

  const loadMy = useCallback(async () => {
    const r = await jsonApi('/hub/submissions/mine/all');
    setMySubs(r?.submissions || []);
  }, []);

  useEffect(() => { loadDepts(); loadMy(); }, [loadDepts, loadMy]);

  const pendingMine = mySubs.filter(s => s.status === 'pending').length;

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8" data-testid="teacher-hub">
      <PageHeading title="DeskeyHUB" description="Browse each department's library, download research, and add your own PDFs and documents" />
      <div className="flex bg-slate-100 rounded-lg p-1 gap-1 w-fit" role="tablist">
        {[
          { id: 'browse', label: 'Browse libraries', icon: FolderOpen },
          { id: 'my-submissions', label: 'My submissions', icon: Send, badge: pendingMine },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} role="tab" aria-selected={tab === t.id} className={`flex items-center gap-2 px-3 h-8 rounded text-sm font-medium transition-colors ${tab === t.id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`teacher-hub-tab-${t.id}`}>
            <t.icon className="w-4 h-4 text-slate-400" /> {t.label}
            {!!t.badge && <span className="text-slate-500 tabular-nums">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'browse' && !selectedDept && (
        loading ? (
          <Loader />
        ) : depts.length === 0 ? (
          <EmptyState icon={Library} title="No libraries you can open yet" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="teacher-hub-depts">
            {depts.map(d => (
              <button key={d.id} onClick={() => setSelectedDept(d)} className={`${CARD} ${CARD_HOVER} p-5 text-left group`} data-testid={`teacher-hub-dept-${d.id}`}>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-green-50 text-[#1A7F37] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <Library className="w-5 h-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-base font-semibold text-slate-900 truncate">{d.name}</h3>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 transition-colors flex-shrink-0" />
                    </div>
                    <p className="text-sm text-slate-600 mt-1 tabular-nums">{d.document_count || 0} {(d.document_count || 0) === 1 ? 'document' : 'documents'} · {d.folder_count || 0} {(d.folder_count || 0) === 1 ? 'folder' : 'folders'}</p>
                    {d.teacher_contributions > 0 && <p className="text-sm text-slate-500 mt-1 tabular-nums">{d.teacher_contributions} from teachers</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {tab === 'browse' && selectedDept && (
        <DeptLibraryView A={A} dept={selectedDept} mySubs={mySubs} onBack={() => setSelectedDept(null)} onSubmitted={loadMy} />
      )}

      {tab === 'my-submissions' && (
        <MySubmissionsView A={A} subs={mySubs} onRefresh={loadMy} />
      )}
    </div>
  );
}

function DeptLibraryView({ A, dept, mySubs = [], onBack, onSubmitted }) {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [search, setSearch] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [autoStatus, setAutoStatus] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);

  const loadSubjects = useCallback(async () => {
    const r = await jsonApi(`/hub/repo/${dept.id}/subjects`);
    setSubjects(r?.subjects || []);
  }, [dept.id]);

  const loadAuto = useCallback(async () => {
    const r = await jsonApi(`/hub/repo/${dept.id}/auto-approval/status`);
    setAutoStatus({ enabled: !!r?.enabled, enabled_by_name: r?.enabled_by_name });
  }, [dept.id]);

  const loadInner = useCallback(async () => {
    if (!selectedSubject) { setFolders([]); setDocs([]); setBreadcrumbs([]); setTotalDocs(0); return; }
    const fParams = new URLSearchParams();
    fParams.set('subject_id', selectedSubject.id);
    if (currentFolder) fParams.set('parent_id', currentFolder.id);
    const dParams = new URLSearchParams();
    dParams.set('subject_id', selectedSubject.id);
    if (currentFolder) dParams.set('folder_id', currentFolder.id);
    if (search.trim()) { dParams.set('q', search.trim()); dParams.set('folder_id', '*'); }
    const tParams = new URLSearchParams();
    tParams.set('subject_id', selectedSubject.id);
    tParams.set('folder_id', '*');
    const [f, d, t] = await Promise.all([
      jsonApi(`/hub/repo/${dept.id}/folders?${fParams}`),
      jsonApi(`/hub/repo/${dept.id}/documents?${dParams}`),
      jsonApi(`/hub/repo/${dept.id}/documents?${tParams}`),
    ]);
    setFolders(f?.folders || []);
    setBreadcrumbs(f?.breadcrumbs || []);
    setDocs(d?.documents || []);
    setTotalDocs((t?.documents || []).length);
  }, [dept.id, currentFolder, search, selectedSubject]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSubjects(), loadAuto()]).finally(() => setLoading(false));
  }, [loadSubjects, loadAuto]);

  useEffect(() => { loadInner(); }, [loadInner]);

  const filteredSubjects = useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    const q = subjectSearch.toLowerCase();
    return subjects.filter(s => (s.name || '').toLowerCase().includes(q) || (s.code || '').toLowerCase().includes(q));
  }, [subjects, subjectSearch]);

  // Uploads that are still awaiting admin review don't appear in `docs` yet
  // (they only publish to hub_documents on approval) — surface a hint here
  // so "my file disappeared" reads as "it's pending review", not "it failed".
  const myPendingHere = useMemo(() => {
    if (!selectedSubject) return 0;
    return mySubs.filter(s => s.status === 'pending' && s.target_department_id === dept.id && s.target_subject_id === selectedSubject.id).length;
  }, [mySubs, dept.id, selectedSubject]);

  return (
    <div className="space-y-3" data-testid="teacher-hub-dept-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="teacher-hub-back"><ArrowLeft className="w-4 h-4 text-slate-600" /></button>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {dept.name}
              {autoStatus.enabled && (
                <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-100">
                  <Zap className="w-2.5 h-2.5" /> Auto-publish on
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-500">{subjects.length} subject{subjects.length === 1 ? '' : 's'}{selectedSubject ? ` · ${totalDocs} doc${totalDocs === 1 ? '' : 's'} in ${selectedSubject.name}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedSubject && (
            <Button size="sm" onClick={() => setShowSubmit(true)} className="shadow-none bg-[#1A7F37] hover:bg-[#166B2E] text-white gap-1.5 h-9 rounded-lg" data-testid="teacher-hub-submit-btn">
              <Upload className="w-3.5 h-3.5" /> Upload
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !selectedSubject ? (
        /* SUBJECTS LEVEL */
        <div className="space-y-3" data-testid="teacher-hub-subjects-level">
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input value={subjectSearch} onChange={e => setSubjectSearch(e.target.value)} placeholder="Search subjects" className="w-full h-9 pl-8 pr-3 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-300 bg-white" data-testid="teacher-hub-subject-search" />
          </div>
          {filteredSubjects.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
              <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">{subjectSearch ? 'No subjects match' : 'No subjects yet'}</p>
              <p className="text-xs text-slate-400 mt-1">{subjectSearch ? '' : 'Your department head adds subjects and folders here.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {filteredSubjects.map(s => {
                const showCode = s.code && s.code.toLowerCase() !== (s.name || '').toLowerCase();
                return (
                  <button key={s.id} onClick={() => setSelectedSubject(s)} className="bg-white border border-slate-200 rounded-xl p-3.5 text-left hover:border-slate-300 transition-colors group" data-testid={`teacher-hub-subject-${s.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-[18px] h-[18px]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.name}</h4>
                          {showCode && <span className="text-xs font-mono text-slate-500">{s.code}</span>}
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 ml-auto transition-colors flex-shrink-0" />
                        </div>
                        {s.description && <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 leading-relaxed">{s.description}</p>}
                        <p className="text-xs text-slate-500 mt-1 tabular-nums">{count(s.document_count, 'document')} · {count(s.folder_count, 'folder')}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* INSIDE A SUBJECT — folders + docs (read-only browsing) */
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs flex-wrap">
              <button onClick={() => { setSelectedSubject(null); setCurrentFolder(null); setSearch(''); }} className="flex items-center gap-1 px-2 py-1 rounded-md text-slate-600 hover:bg-slate-100 transition-colors" data-testid="teacher-hub-crumb-subjects"><BookOpen className="w-3 h-3" /> Subjects</button>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <button onClick={() => setCurrentFolder(null)} className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${!currentFolder ? 'font-medium text-slate-900 bg-slate-100' : 'text-slate-600 hover:bg-slate-100'}`}>
                {selectedSubject.name}{selectedSubject.code && selectedSubject.code.toLowerCase() !== selectedSubject.name.toLowerCase() ? ` (${selectedSubject.code})` : ''}
              </button>
              {breadcrumbs.map((c, i) => (
                <React.Fragment key={c.id}>
                  <ChevronRight className="w-3 h-3 text-slate-300" />
                  <button onClick={() => setCurrentFolder(c)} className={`px-2 py-1 rounded-md ${i === breadcrumbs.length - 1 ? 'font-medium text-slate-900 bg-slate-100' : 'text-slate-600 hover:bg-slate-100'}`}>{c.name}</button>
                </React.Fragment>
              ))}
            </div>
            <div className="relative ml-auto max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-full h-9 pl-8 pr-3 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-300 bg-white" data-testid="teacher-hub-search" />
            </div>
          </div>

          {!search.trim() && folders.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Folders</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                {folders.map(f => (
                  <button key={f.id} onClick={() => setCurrentFolder({ id: f.id, name: f.name })} className="bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-colors flex items-center gap-2.5" data-testid={`teacher-hub-folder-${f.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0"><FolderOpen className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.name}</p>
                      <p className="text-xs text-slate-500 tabular-nums">{count(f.document_count, 'document')}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {myPendingHere > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-800">You have {myPendingHere} submission{myPendingHere === 1 ? '' : 's'} awaiting admin review in this subject — track status in <strong>My Submissions</strong>.</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Documents {search.trim() && `— "${search}"`}</p>
            {docs.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
                <FileText className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-500 font-medium">No documents {search ? 'match' : 'in this subject yet'}</p>
                {!search && (
                  <Button size="sm" onClick={() => setShowSubmit(true)} className="mt-3 gap-1.5 h-9 rounded-lg shadow-none bg-[#1A7F37] hover:bg-[#166B2E] text-white" data-testid="teacher-hub-empty-submit"><Send className="w-3.5 h-3.5" /><span>{autoStatus.enabled ? 'Publish first document' : 'Submit first contribution'}</span></Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map(d => {
                  const f = fileBadge(d.mime_type, d.file_name);
                  return (
                    <div key={d.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-colors" data-testid={`teacher-hub-doc-${d.id}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-medium" style={{ background: f.bg }}>{f.label}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{d.title}</p>
                          {d.is_teacher_contribution && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">From a teacher</span>}
                          {d.auto_approved && <span className="text-xs font-medium bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-100 flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> auto</span>}
                        </div>
                        {d.description && <p className="text-xs text-slate-600 line-clamp-1">{d.description}</p>}
                        <p className="text-xs text-slate-500">{d.uploader_name} · {fmtBytes(d.file_size)} · {fmtDate(d.published_at || d.created_at)}</p>
                      </div>
                      <button onClick={() => setPreviewDoc(d)} className="text-xs font-medium text-slate-700 hover:bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors" data-testid={`teacher-hub-doc-view-${d.id}`}>
                        <Eye className="w-3 h-3" /> View
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {showSubmit && (
        <TeacherSubmitModal
          A={A}
          dept={dept}
          subject={selectedSubject}
          folders={[currentFolder, ...folders].filter(Boolean)}
          currentFolderId={currentFolder?.id}
          autoApprovalOn={autoStatus.enabled}
          onClose={() => setShowSubmit(false)}
          onDone={() => { setShowSubmit(false); loadInner(); loadSubjects(); onSubmitted?.(); }}
        />
      )}
      {previewDoc && (
        <DocViewer doc={previewDoc} departmentId={dept.id} onClose={() => setPreviewDoc(null)} accent={A} />
      )}
    </div>
  );
}

function TeacherDocPreview({ A, dept, doc, onClose }) {
  const download = async () => {
    const token = localStorage.getItem('desky_token');
    const res = await fetch(`${API}/api/hub/repo/${dept.id}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error('Download failed. Please try again.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = doc.file_name || 'document'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const f = fileBadge(doc.mime_type, doc.file_name);
  const isPdf = f.label === 'PDF';
  const isImage = f.label === 'IMG';
  const canPreviewInline = isPdf || isImage;
  // dl=0 serves Content-Disposition: inline; fetched with the auth header and
  // shown as a blob: URL since an <iframe>/<img> can't send the header.
  const [viewUrl, viewFailed] = useAuthedObjectUrl(
    canPreviewInline ? `${API}/api/hub/repo/${dept.id}/documents/${doc.id}/download?dl=0` : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-preview">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl w-full flex flex-col ${canPreviewInline ? 'max-w-4xl h-[88vh]' : 'max-w-lg'}`}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-medium" style={{ background: f.bg }}>{f.label}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{doc.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{doc.file_name} · {fmtBytes(doc.file_size)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {canPreviewInline && (
          <div className="flex-1 min-h-0 bg-slate-100 flex items-center justify-center overflow-auto">
            {!viewUrl ? (
              viewFailed
                ? <p className="text-sm text-slate-500">Could not load file.</p>
                : <Loader />
            ) : isPdf ? (
              <iframe src={viewUrl} title={doc.title} className="w-full h-full border-0" data-testid="teacher-hub-preview-frame" />
            ) : (
              <img src={viewUrl} alt={doc.title} className="max-w-full max-h-full object-contain" data-testid="teacher-hub-preview-image" />
            )}
          </div>
        )}

        <div className="p-5 space-y-2 text-xs text-slate-700 flex-shrink-0">
          {doc.description && <p>{doc.description}</p>}
          <p className="text-xs text-slate-500">Uploaded by <span className="font-medium text-slate-700">{doc.uploader_name}</span> · {fmtDate(doc.published_at || doc.created_at)}</p>
          {(doc.tags || []).length > 0 && <div className="flex items-center gap-1 flex-wrap">{doc.tags.map((t, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t}</span>)}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" onClick={download} className="shadow-none bg-[#1A7F37] hover:bg-[#166B2E] text-white gap-1.5 rounded-lg" data-testid="teacher-hub-download"><Download className="w-3.5 h-3.5" /> Download</Button>
        </div>
      </div>
    </div>
  );
}

function TeacherSubmitModal({ A, dept, subject, folders, currentFolderId, autoApprovalOn, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [folderId, setFolderId] = useState(currentFolderId || folders[0]?.id || '');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { setErr('File must be under 25 MB'); return; }
    setErr(''); setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setErr('Pick a file'); return; }
    if (!title.trim()) { setErr('Title required'); return; }
    if (!folderId) { setErr('Pick the folder to upload into'); return; }
    setSaving(true); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('description', description.trim());
    fd.append('folder_id', folderId);  // the folder decides the subject server-side
    if (tags.trim()) fd.append('tags', tags.trim());
    const r = await formApi(`/hub/submissions/${dept.id}`, fd);
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    toast.success(r?.auto_approved ? 'Published to the library' : 'Submitted — awaiting admin review', {
      description: r?.auto_approved ? undefined : 'Track its status under My Submissions.',
    });
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-submit-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Send className="w-4 h-4" style={{ color: A }} /> {autoApprovalOn ? 'Publish' : 'Submit'} to {subject ? subject.name : dept.name}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {autoApprovalOn ? (
            <div className="bg-green-50 border border-green-100 rounded-lg p-2.5 flex items-start gap-2">
              <Zap className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-800 leading-relaxed">This department has <strong>auto-publish ON</strong>. Your file will appear in the library immediately — no review queue.</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800 leading-relaxed">A department admin will approve or request changes before this is published. Track status in <strong>My Submissions</strong>.</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-500">File <span className="text-red-500">*</span></label>
            <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-xl p-5 text-center cursor-pointer hover:border-[#34A853]/60 transition-colors" onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }} data-testid="teacher-hub-submit-dropzone">
              <input ref={inputRef} type="file" onChange={e => pick(e.target.files?.[0])} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp" data-testid="teacher-hub-submit-file" />
              {file ? <div><FileText className="w-7 h-7 mx-auto mb-1.5" style={{ color: A }} /><p className="text-xs font-medium text-slate-900 truncate">{file.name}</p><p className="text-xs text-slate-500 mt-0.5">{fmtBytes(file.size)}</p></div> : <div><Upload className="w-7 h-7 text-slate-300 mx-auto mb-1.5" /><p className="text-xs text-slate-600 font-medium">Click or drop a file</p><p className="text-xs text-slate-400 mt-0.5">PDF, DOC, images — up to 25 MB</p></div>}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Title <span className="text-red-500">*</span></label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="What's this document about?" data-testid="teacher-hub-submit-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1.5 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" placeholder="Context for the reviewer" data-testid="teacher-hub-submit-desc" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Folder <span className="text-red-500">*</span></label>
            {folders.length > 0 ? (
              <select value={folderId} onChange={e => setFolderId(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-slate-200 px-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" data-testid="teacher-hub-submit-folder">
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            ) : (
              <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5" data-testid="teacher-hub-submit-no-folder">
                No folders here yet. Ask your department head to create one.
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Tags</label>
            <input value={tags} onChange={e => setTags(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="research, physics, class-11" data-testid="teacher-hub-submit-tags" />
          </div>
          {err && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving || !file || !folderId} className="shadow-none bg-[#1A7F37] hover:bg-[#166B2E] text-white gap-1.5 rounded-lg" data-testid="teacher-hub-submit-do">
            {saving ? (autoApprovalOn ? 'Publishing...' : 'Submitting...') : <><Send className="w-3.5 h-3.5" /> {autoApprovalOn ? 'Publish Now' : 'Submit for Review'}</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function MySubmissionsView({ A, subs, onRefresh }) {
  const filt = useMemo(() => subs, [subs]);
  if (filt.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl" data-testid="teacher-hub-my-empty">
        <Send className="w-12 h-12 text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-500 font-medium">You haven't submitted anything yet</p>
        <p className="text-xs text-slate-400 mt-1">Browse a department library and click "Contribute to this Library" to share your PDFs, notes, or research.</p>
      </div>
    );
  }
  const statusMeta = {
    pending: { bg: 'bg-amber-50 text-amber-700 border-amber-100', icon: Clock },
    approved: { bg: 'bg-green-50 text-green-700 border-green-100', icon: CheckCircle },
    rejected: { bg: 'bg-red-50 text-red-700 border-red-100', icon: XCircle },
    changes_requested: { bg: 'bg-blue-50 text-blue-700 border-blue-100', icon: AlertCircle },
  };
  return (
    <div className="space-y-2" data-testid="teacher-hub-my-subs">
      {filt.map(s => {
        const meta = statusMeta[s.status] || statusMeta.pending;
        const Icon = meta.icon;
        const f = fileBadge(s.mime_type, s.file_name);
        return (
          <div key={s.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3 transition-colors" data-testid={`teacher-hub-my-sub-${s.id}`}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-medium" style={{ background: f.bg }}>{f.label}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</p>
                <span className={`text-xs font-medium capitalize px-1.5 py-0.5 rounded border flex items-center gap-1 ${meta.bg}`}><Icon className="w-2.5 h-2.5" /> {s.status.replace('_', ' ')}</span>
              </div>
              <p className="text-xs text-slate-500">to <span className="font-medium text-slate-700">{s.target_department_name || 'department'}</span> · {fmtBytes(s.file_size)} · submitted {fmtDate(s.submitted_at)}</p>
              {s.reviewer_notes && <p className="text-sm text-slate-600 mt-0.5">Reviewer: &ldquo;{s.reviewer_notes}&rdquo;</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
